"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowDownRight, Download, FileText } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Highlighter } from "@/components/ui/highlighter";
import SplitText from "@/components/animata/text/split-text";
import GlitchText from "@/components/GlitchText";
import HeroCarabiner3D from "@/components/HeroCarabiner3D";
import EgyptMapTooltip from "@/components/EgyptMapTooltip";
import { CoolMode } from "@/components/ui/cool-mode";
import { useNeumorphicTheme } from "@/hooks/useNeumorphicTheme";
import { useEmberTheme } from "@/hooks/useEmberTheme";

// --- HERO DESCRIPTION RENDERER ---
// Parses the description string (which may contain <br/> tags) and wraps
// the word "Egypt" or "مصر" with the interactive EgyptMapTooltip.
function HeroDescription(t0) {
  const $ = _c(8);
  const {
    html,
    className
  } = t0;
  let t1;
  let t2;
  if ($[0] !== className || $[1] !== html) {
    const lines = html.split(/<br\s*\/?>/);
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      const EGYPT_REGEX = /(Egypt|مصر)/g;
      t3 = (line, lineIdx) => {
        const parts = line.split(EGYPT_REGEX);
        return parts.map((part, partIdx) => {
          if (part === "Egypt" || part === "\u0645\u0635\u0631") {
            return <EgyptMapTooltip key={`${lineIdx}-${partIdx}`}><span className="underline decoration-4 decoration-zinc-800 underline-offset-2 cursor-help">{part}</span></EgyptMapTooltip>;
          }
          return <span key={`${lineIdx}-${partIdx}`}>{part}</span>;
        });
      };
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    const renderLine = t3;
    t1 = className;
    t2 = lines.map((line_0, idx) => <span key={idx}>{renderLine(line_0, idx)}{idx < lines.length - 1 && <br />}</span>);
    $[0] = className;
    $[1] = html;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <p className={t1}>{t2}</p>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export default function Hero(t0) {
  const $ = _c(95);
  const {
    dict
  } = t0;
  const isNeumorphic = useNeumorphicTheme();
  const isEmber = useEmberTheme();
  let t1;
  if ($[0] !== isEmber || $[1] !== isNeumorphic) {
    t1 = isDarkButton => {
      if (isEmber) {
        return isDarkButton ? "#1BADF0" : "#facc15";
      }
      if (isNeumorphic) {
        return isDarkButton ? "#ffffff" : "#94a3b8";
      }
      return isDarkButton ? "#facc15" : "#ef4444";
    };
    $[0] = isEmber;
    $[1] = isNeumorphic;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const getHighlightColor = t1;
  const [isWorkHovered, setIsWorkHovered] = useState(false);
  const [isContactHovered, setIsContactHovered] = useState(false);
  const [isCvHovered, setIsCvHovered] = useState(false);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <HeroCarabiner3D />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `text-[clamp(0.65rem,3.8vw,1.25rem)] whitespace-nowrap font-bold uppercase tracking-widest border-b-4 pb-1 ${isNeumorphic ? "bg-[#e0e5ec] border-[#a3b1c6] text-[#4b5563]" : "text-white border-white bg-transparent"}`;
  const t4 = dict?.availability || "Available: Full-Time / Freelance";
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <div className="flex items-center gap-4 mb-4 md:mb-6"><span className={t3}>{t4}</span></div>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = dict?.description || "Full-Stack Developer. <br/> Next.js \u2022 Spring Boot \u2022 MongoDB \u2022 Docker <br/> Based in Egypt. <br/> Building web apps & APIs.";
  const t7 = `hidden md:block text-lg md:text-2xl font-bold max-w-2xl leading-snug -ms-2 ps-2 hero-subtitle-backdrop mb-6 ${isNeumorphic ? "text-[#2d3748]" : "text-white/80"}`;
  let t8;
  if ($[7] !== t6 || $[8] !== t7) {
    t8 = <HeroDescription html={t6} className={t7} />;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = dict?.samy || "Samy";
  let t10;
  if ($[10] !== t9) {
    t10 = <SplitText text={t9} className="text-white hero-samy-text z-10" />;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <br />;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const t12 = dict?.barsoum || "Barsoum";
  let t13;
  if ($[13] !== t12) {
    t13 = <span className="absolute inset-0 text-white px-2 sm:px-4 z-0 pointer-events-none flex items-center" aria-hidden="true"><GlitchText text={t12} /></span>;
    $[13] = t12;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const t14 = dict?.barsoum || "Barsoum";
  let t15;
  if ($[15] !== t14) {
    t15 = <span className="absolute inset-0 bg-white text-black px-2 sm:px-4 z-10 overflow-hidden flex items-center"><GlitchText text={t14} /></span>;
    $[15] = t14;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  const t16 = dict?.barsoum || "Barsoum";
  let t17;
  if ($[17] !== t16) {
    t17 = <span className="relative invisible px-2 sm:px-4 z-[-1] pointer-events-none flex items-center" aria-hidden="true"><GlitchText text={t16} /></span>;
    $[17] = t16;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== t13 || $[20] !== t15 || $[21] !== t17) {
    t18 = <span className="inline-flex mt-4 md:mt-4 transform -skew-x-6 z-20 relative">{t13}{t15}{t17}</span>;
    $[19] = t13;
    $[20] = t15;
    $[21] = t17;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== t10 || $[24] !== t18) {
    t19 = <h1 className="text-[15vw] sm:text-6xl md:text-8xl lg:text-[7.5rem] xl:text-[9rem] font-black uppercase tracking-tighter leading-[0.85]">{t10}{t11}{t18}</h1>;
    $[23] = t10;
    $[24] = t18;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  const t20 = `w-12 h-1 mb-3 ${isNeumorphic ? "bg-zinc-400" : "bg-white/40"}`;
  let t21;
  if ($[26] !== t20) {
    t21 = <div className={t20} />;
    $[26] = t20;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  const t22 = dict?.description || "Full-Stack Developer. <br/> Next.js \u2022 Spring Boot \u2022 MongoDB \u2022 Docker <br/> Based in Egypt. <br/> Building web apps & APIs.";
  const t23 = `text-sm font-semibold leading-relaxed tracking-wide hero-subtitle-backdrop ${isNeumorphic ? "text-[#2d3748]" : "text-white/75"}`;
  let t24;
  if ($[28] !== t22 || $[29] !== t23) {
    t24 = <HeroDescription html={t22} className={t23} />;
    $[28] = t22;
    $[29] = t23;
    $[30] = t24;
  } else {
    t24 = $[30];
  }
  let t25;
  if ($[31] !== t21 || $[32] !== t24) {
    t25 = <div className="block md:hidden mt-4">{t21}{t24}</div>;
    $[31] = t21;
    $[32] = t24;
    $[33] = t25;
  } else {
    t25 = $[33];
  }
  let t26;
  if ($[34] !== t19 || $[35] !== t25 || $[36] !== t5 || $[37] !== t8) {
    t26 = <div className="animate-slide-up relative z-10">{t5}{t8}{t19}{t25}</div>;
    $[34] = t19;
    $[35] = t25;
    $[36] = t5;
    $[37] = t8;
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  const t27 = `flex flex-col w-full gap-3 md:gap-6 border-t-0 border-s-0 min-[1300px]:border-s-8 min-[1300px]:ps-12 min-[1300px]:py-8 animate-slide-up-delay-1 relative z-0 ${isNeumorphic ? "border-black" : "border-white"}`;
  let t28;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      particle: "brutalist-cv"
    };
    $[39] = t28;
  } else {
    t28 = $[39];
  }
  let t29;
  let t30;
  let t31;
  let t32;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setIsCvHovered(true);
    t30 = () => setIsCvHovered(false);
    t31 = () => setIsCvHovered(true);
    t32 = () => setIsCvHovered(false);
    $[40] = t29;
    $[41] = t30;
    $[42] = t31;
    $[43] = t32;
  } else {
    t29 = $[40];
    t30 = $[41];
    t31 = $[42];
    t32 = $[43];
  }
  const t33 = `relative flex justify-between items-center w-full px-4 py-3 md:p-8 text-lg md:text-3xl font-black uppercase transition-all duration-300 ease-in-out group ${isNeumorphic ? "bg-[#e0e5ec] text-[#4b5563] rounded-2xl border border-transparent shadow-[6px_6px_12px_rgba(163,177,198,0.6),_-6px_-6px_12px_rgba(255,255,255,0.5)] hover:bg-[#d1d9e6] hover:text-[#1e293b] hover:shadow-[8px_8px_16px_rgba(163,177,198,0.7),_-8px_-8px_16px_rgba(255,255,255,0.6)] active:shadow-[inset_4px_4px_8px_rgba(163,177,198,0.6),_inset_-4px_-4px_8px_rgba(255,255,255,0.5)]" : "bg-white text-black border-4 border-white shadow-[8px_8px_0px_rgba(255,255,255,0.3)] hover:bg-black hover:text-white hover:border-white hover:shadow-[4px_4px_0px_rgba(255,255,255,0.3)] hover:translate-x-1 hover:translate-y-1 active:shadow-none active:translate-x-2 active:translate-y-2"}`;
  const t34 = getHighlightColor(true);
  const t35 = dict?.getCV || "Get CV";
  let t36;
  if ($[44] !== isCvHovered || $[45] !== t34 || $[46] !== t35) {
    t36 = <Highlighter show={isCvHovered} action="highlight" color={t34}>{t35}</Highlighter>;
    $[44] = isCvHovered;
    $[45] = t34;
    $[46] = t35;
    $[47] = t36;
  } else {
    t36 = $[47];
  }
  const t37 = `absolute inset-0 w-full h-full transition-all duration-500 ${isCvHovered ? "opacity-0 scale-50 translate-y-4" : ""}`;
  let t38;
  if ($[48] !== t37) {
    t38 = <Download className={t37} />;
    $[48] = t37;
    $[49] = t38;
  } else {
    t38 = $[49];
  }
  const t39 = `absolute inset-0 w-full h-full transition-all duration-500 ${isCvHovered ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-50 -translate-y-4"}`;
  let t40;
  if ($[50] !== t39) {
    t40 = <FileText className={t39} />;
    $[50] = t39;
    $[51] = t40;
  } else {
    t40 = $[51];
  }
  let t41;
  if ($[52] !== t38 || $[53] !== t40) {
    t41 = <div className="relative w-6 h-6 md:w-10 md:h-10">{t38}{t40}</div>;
    $[52] = t38;
    $[53] = t40;
    $[54] = t41;
  } else {
    t41 = $[54];
  }
  let t42;
  if ($[55] !== t33 || $[56] !== t36 || $[57] !== t41) {
    t42 = <CoolMode options={t28}><a href="/Samy_Barsoum_CV.pdf" download="Samy_Barsoum_CV.pdf" onMouseEnter={t29} onMouseLeave={t30} onTouchStart={t31} onTouchEnd={t32} className={t33}>{t36}{t41}</a></CoolMode>;
    $[55] = t33;
    $[56] = t36;
    $[57] = t41;
    $[58] = t42;
  } else {
    t42 = $[58];
  }
  let t43;
  let t44;
  let t45;
  let t46;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = () => setIsWorkHovered(true);
    t44 = () => setIsWorkHovered(false);
    t45 = () => setIsWorkHovered(true);
    t46 = () => setIsWorkHovered(false);
    $[59] = t43;
    $[60] = t44;
    $[61] = t45;
    $[62] = t46;
  } else {
    t43 = $[59];
    t44 = $[60];
    t45 = $[61];
    t46 = $[62];
  }
  const t47 = `relative flex justify-between items-center w-full px-4 py-3 md:p-8 text-lg md:text-3xl font-black uppercase transition-all duration-300 ease-in-out group ${isNeumorphic ? "bg-[#e0e5ec] text-[#4b5563] rounded-2xl border border-transparent shadow-[6px_6px_12px_rgba(163,177,198,0.6),_-6px_-6px_12px_rgba(255,255,255,0.5)] hover:bg-[#d1d9e6] hover:text-[#1e293b] hover:shadow-[8px_8px_16px_rgba(163,177,198,0.7),_-8px_-8px_16px_rgba(255,255,255,0.6)] active:shadow-[inset_4px_4px_8px_rgba(163,177,198,0.6),_inset_-4px_-4px_8px_rgba(255,255,255,0.5)]" : "bg-transparent text-white border-4 border-white shadow-[8px_8px_0px_rgba(255,255,255,0.2)] hover:bg-white hover:text-black hover:shadow-[4px_4px_0px_rgba(255,255,255,0.2)] hover:translate-x-1 hover:translate-y-1 active:shadow-none active:translate-x-2 active:translate-y-2"}`;
  const t48 = getHighlightColor(false);
  const t49 = dict?.work || "Projects";
  let t50;
  if ($[63] !== isWorkHovered || $[64] !== t48 || $[65] !== t49) {
    t50 = <Highlighter show={isWorkHovered} action="box" color={t48}>{t49}</Highlighter>;
    $[63] = isWorkHovered;
    $[64] = t48;
    $[65] = t49;
    $[66] = t50;
  } else {
    t50 = $[66];
  }
  const t51 = `w-6 h-6 md:w-10 md:h-10 transition-transform rtl:-scale-x-100 ${isWorkHovered ? "[animation:arrow-snap-sequence_0.8s_ease-in-out_forwards]" : ""}`;
  let t52;
  if ($[67] !== t51) {
    t52 = <ArrowDownRight className={t51} />;
    $[67] = t51;
    $[68] = t52;
  } else {
    t52 = $[68];
  }
  let t53;
  if ($[69] !== t47 || $[70] !== t50 || $[71] !== t52) {
    t53 = <Link href="#projects" onMouseEnter={t43} onMouseLeave={t44} onTouchStart={t45} onTouchEnd={t46} onClick={_temp} className={t47}>{t50}{t52}</Link>;
    $[69] = t47;
    $[70] = t50;
    $[71] = t52;
    $[72] = t53;
  } else {
    t53 = $[72];
  }
  let t54;
  let t55;
  let t56;
  let t57;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = () => setIsContactHovered(true);
    t55 = () => setIsContactHovered(false);
    t56 = () => setIsContactHovered(true);
    t57 = () => setIsContactHovered(false);
    $[73] = t54;
    $[74] = t55;
    $[75] = t56;
    $[76] = t57;
  } else {
    t54 = $[73];
    t55 = $[74];
    t56 = $[75];
    t57 = $[76];
  }
  const t58 = `relative flex justify-between items-center w-full px-4 py-3 md:p-8 text-lg md:text-3xl font-black uppercase transition-all duration-300 ease-in-out group ${isNeumorphic ? "bg-[#e0e5ec] text-[#4b5563] rounded-2xl border border-transparent shadow-[6px_6px_12px_rgba(163,177,198,0.6),_-6px_-6px_12px_rgba(255,255,255,0.5)] hover:bg-[#d1d9e6] hover:text-[#1e293b] hover:shadow-[8px_8px_16px_rgba(163,177,198,0.7),_-8px_-8px_16px_rgba(255,255,255,0.6)] active:shadow-[inset_4px_4px_8px_rgba(163,177,198,0.6),_inset_-4px_-4px_8px_rgba(255,255,255,0.5)]" : "bg-transparent text-white border-4 border-white shadow-[8px_8px_0px_rgba(255,255,255,0.2)] hover:bg-white hover:text-black hover:shadow-[4px_4px_0px_rgba(255,255,255,0.2)] hover:translate-x-1 hover:translate-y-1 active:shadow-none active:translate-x-2 active:translate-y-2"}`;
  const t59 = getHighlightColor(false);
  const t60 = dict?.contact || "Contact";
  let t61;
  if ($[77] !== isContactHovered || $[78] !== t59 || $[79] !== t60) {
    t61 = <Highlighter show={isContactHovered} action="circle" color={t59}>{t60}</Highlighter>;
    $[77] = isContactHovered;
    $[78] = t59;
    $[79] = t60;
    $[80] = t61;
  } else {
    t61 = $[80];
  }
  const t62 = `w-6 h-6 md:w-10 md:h-10 transition-transform rtl:-scale-x-100 ${isContactHovered ? "[animation:arrow-snap-sequence_0.8s_ease-in-out_forwards]" : ""}`;
  let t63;
  if ($[81] !== t62) {
    t63 = <ArrowDownRight className={t62} />;
    $[81] = t62;
    $[82] = t63;
  } else {
    t63 = $[82];
  }
  let t64;
  if ($[83] !== t58 || $[84] !== t61 || $[85] !== t63) {
    t64 = <Link href="#contact" onMouseEnter={t54} onMouseLeave={t55} onTouchStart={t56} onTouchEnd={t57} onClick={_temp2} className={t58}>{t61}{t63}</Link>;
    $[83] = t58;
    $[84] = t61;
    $[85] = t63;
    $[86] = t64;
  } else {
    t64 = $[86];
  }
  let t65;
  if ($[87] !== t27 || $[88] !== t42 || $[89] !== t53 || $[90] !== t64) {
    t65 = <div className={t27}>{t42}{t53}{t64}</div>;
    $[87] = t27;
    $[88] = t42;
    $[89] = t53;
    $[90] = t64;
    $[91] = t65;
  } else {
    t65 = $[91];
  }
  let t66;
  if ($[92] !== t26 || $[93] !== t65) {
    t66 = <section id="hero" className="relative min-h-[100svh] flex flex-col justify-center items-start px-6 md:px-12 lg:px-24 border-b-8 border-black pt-24 md:pt-28 pb-8 md:pb-11 overflow-hidden bg-black">{t2}<div className="relative z-10 w-full max-w-[75rem] mx-auto grid grid-cols-1 min-[1300px]:grid-cols-[1fr_28rem] min-[1300px]:items-center gap-5 sm:gap-8 min-[1300px]:gap-16">{t26}{t65}</div></section>;
    $[92] = t26;
    $[93] = t65;
    $[94] = t66;
  } else {
    t66 = $[94];
  }
  return t66;
}
function _temp2(e_0) {
  e_0.preventDefault();
  document.getElementById("contact")?.scrollIntoView({
    behavior: "smooth"
  });
  window.history.pushState(null, "", "#contact");
}
function _temp(e) {
  e.preventDefault();
  document.getElementById("projects")?.scrollIntoView({
    behavior: "smooth"
  });
  window.history.pushState(null, "", "#projects");
}